import { ImageWithFallback } from '@/components/ImageWithFallback';
import { entityCardMessages } from './entityCard.messages';
import { IconButton } from '@/components/IconButton';
import { useState, useRef, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';
import { Small } from '@/components/Typography';

interface EntityDetail {
  label: string;
  value: string | number | undefined | null;
}

interface EntityAction {
  actionButtonLabel: string;
  onButtonClick: () => void;
}

export interface EntityCardProps {
  title: string;
  description?: string | null;
  imageUrls?: string[];
  imageAlt?: string;
  tags?: string[];
  details?: EntityDetail[];
  actions?: EntityAction[];
  className?: string;
}

export function EntityCard({
  title,
  description,
  imageUrls,
  imageAlt,
  tags,
  details,
  actions,
  className,
}: EntityCardProps) {
  const [isHovering, setIsHovering] = useState(false);
  const [api, setApi] = useState<CarouselApi>();
  const cardRef = useRef<HTMLDivElement>(null);

  // Create unique carousel autoplay plugin instance for each card
  // required to allow cycling on hover with shadcn carousel
  const autoplayRef = useRef<ReturnType<typeof Autoplay> | null>(null);

  // Initialize autoplay plugin with proper cleanup for hot reload
  useEffect(() => {
    autoplayRef.current = Autoplay({
      delay: 1200,
      stopOnInteraction: false,
      stopOnMouseEnter: false, // We'll handle this manually
    });

    // Cleanup on unmount or hot reload
    return () => {
      if (autoplayRef.current) {
        autoplayRef.current.stop();
        autoplayRef.current = null;
      }
    };
  }, []);

  // Control carousel autoplay on hover (only on devices that support hover)
  useEffect(() => {
    if (!api || !imageUrls || imageUrls.length <= 1 || !autoplayRef.current)
      return;

    // Check if device supports hover (desktop/laptop, not touch devices)
    // to prevent autoplay on page load on touch devices
    const supportsHover = window.matchMedia('(hover: hover)').matches;

    if (supportsHover && isHovering) {
      autoplayRef.current.play();
    } else {
      autoplayRef.current.stop();
    }
  }, [api, isHovering, imageUrls]);

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  const baseCardClasses =
    'py-3 group relative flex flex-col aspect-[3/4] w-full overflow-hidden rounded-lg shadow-none hover:ring-4 hover:ring-ring hover:ring-offset-1 hover:ring-offset-background transition-all duration-200 ease-in-out';

  return (
    <Card
      ref={cardRef}
      className={`${baseCardClasses} ${className || ''}`.trim()}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Image Background with Carousel */}
      {imageUrls && imageUrls.length > 0 ? (
        <div className="absolute inset-0 h-full w-full">
          <Carousel
            setApi={setApi}
            plugins={autoplayRef.current ? [autoplayRef.current] : []}
            // ugly selectors needed to make carousel expand to full container size
            className="h-full w-full [&_.overflow-hidden]:!h-full [&_[data-slot=carousel-content]]:!h-full"
            opts={{
              loop: true,
              skipSnaps: false,
              duration: 90,
              dragFree: false,
            }}
          >
            <CarouselContent className="-ml-0 h-full">
              {imageUrls.map((imageUrl, index) => (
                <CarouselItem key={index} className="h-full basis-full pl-0">
                  <ImageWithFallback
                    src={imageUrl}
                    alt={`${imageAlt} ${index + 1}`}
                    className="h-full w-full object-cover"
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>
      ) : (
        <div className="absolute inset-0 h-full w-full">
          <ImageWithFallback
            src={undefined}
            alt={entityCardMessages.noImageAvailable}
          />
        </div>
      )}

      {/* Gradient Overlay */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-t from-black/80 via-black/10 to-transparent"></div>

      {/* Tags (Top Left) */}
      {tags && tags.length > 0 && (
        <div className="absolute left-2.5 top-2.5 z-[3] flex flex-wrap gap-1">
          {tags.slice(0, 2).map((tag, index) => (
            <Badge
              key={index}
              className="border border-white/50 bg-white/70 px-2 py-0.5 text-[10px] text-stone-900 backdrop-blur-sm"
            >
              {tag}
            </Badge>
          ))}
        </div>
      )}

      {/* Actions Dropdown Menu (Top Right) */}
      {actions && actions.length > 0 && (
        <div className="absolute right-1.5 top-1.5 z-[3]">
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <IconButton
                icon="MoreHorizontal"
                buttonVariant="secondary"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent click from bubbling to the Card's onClick
                }}
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {actions.map((action, index) => (
                <DropdownMenuItem
                  key={index}
                  onClick={action.onButtonClick}
                  className="cursor-pointer justify-end"
                >
                  {action.actionButtonLabel}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Content Overlay */}
      <div className="relative z-[2] mt-auto flex flex-grow flex-col p-3.5 text-white">
        <div className="mt-auto w-full">
          <div className="mr-8 overflow-hidden">
            <CardHeader className="mb-0.5 p-0">
              <CardTitle className="line-clamp-3 text-xl font-bold leading-tight text-white">
                {title}
              </CardTitle>
              {description && (
                <CardDescription className="mt-0.5 line-clamp-2 text-xs text-stone-200">
                  {description}
                </CardDescription>
              )}
            </CardHeader>
            {details && details.length > 0 && (
              <CardContent className="mt-1.5 p-0">
                <div className="space-y-0.5 text-xs text-stone-300">
                  {details.slice(0, 2).map((detail, index) =>
                    detail.value ? (
                      <div key={index} className="truncate">
                        <Small className="mr-1 font-medium text-stone-100">
                          {detail.label}:
                        </Small>
                        {String(detail.value)}
                      </div>
                    ) : null
                  )}
                </div>
              </CardContent>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
